import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Search,
  PlayCircle,
  FileText,
  BookOpen,
  Settings,
  Grid,
  List,
  Plus
} from "lucide-react";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FluxoTarefasModal } from '@/components/Tarefas/FluxoTarefasModal';
import { GerenciarTemplatesModal } from '@/components/Tarefas/GerenciarTemplatesModal';
import { PreencherFormularioModal } from '@/components/Tarefas/PreencherFormularioModal';
import { AdicionarTarefaModal } from '@/components/Tarefas/AdicionarTarefaModal';
import { Tarefa } from '@/types/tarefas';
import { TarefaListItem } from '@/components/Tarefas/TarefaListItem';
import { TarefasKanbanBoard } from '@/components/Tarefas/TarefasKanbanBoard';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useIsMobile } from '@/hooks/use-mobile';

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'concluida':
      return <Badge className="bg-green-500 text-white"><CheckCircle2 className="h-3 w-3 mr-1" />Concluída</Badge>;
    case 'concluida_com_pendencias':
      return <Badge className="bg-yellow-600 text-white"><AlertCircle className="h-3 w-3 mr-1" />Concluída com Pendências</Badge>;
    case 'em_andamento':
      return <Badge className="bg-blue-500 text-white"><PlayCircle className="h-3 w-3 mr-1" />Em Andamento</Badge>;
    case 'atrasada':
      return <Badge className="bg-red-500 text-white"><AlertCircle className="h-3 w-3 mr-1" />Atrasada</Badge>;
    case 'pendente':
      return <Badge className="bg-yellow-500 text-white"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
    case 'cancelada':
      return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Cancelada</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

const getPrioridadeBadge = (prioridade: string) => {
  switch (prioridade) {
    case 'urgente':
      return <Badge className="bg-red-600 text-white">Urgente</Badge>;
    case 'alta':
      return <Badge className="bg-orange-500 text-white">Alta</Badge>;
    case 'media':
      return <Badge className="bg-yellow-500 text-white">Média</Badge>;
    case 'baixa':
      return <Badge variant="secondary">Baixa</Badge>;
    default:
      return <Badge variant="secondary">{prioridade}</Badge>;
  }
};

export default function Tarefas() {
  const { hasRole } = useAuthorization();
  const isAdmin = hasRole('admin');
  const isMobile = useIsMobile();
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPrioridade, setFilterPrioridade] = useState<string>('all');
  const [filterEtapa, setFilterEtapa] = useState<string>('all');
  const [filterLeadId, setFilterLeadId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFluxoModal, setShowFluxoModal] = useState(false);
  const [showGerenciarModal, setShowGerenciarModal] = useState(false);
  const [showFormularioModal, setShowFormularioModal] = useState(false);
  const [showAdicionarTarefaModal, setShowAdicionarTarefaModal] = useState(false);
  const [tarefaSelecionada, setTarefaSelecionada] = useState<Tarefa | null>(null);
  const [formularioLoading, setFormularioLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [tarefaParaDeletar, setTarefaParaDeletar] = useState<string | null>(null);
  const [deletandoTarefa, setDeletandoTarefa] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPrioridade !== 'all') params.append('prioridade', filterPrioridade);
      if (filterEtapa !== 'all') params.append('etapa', filterEtapa);
      if (filterLeadId) params.append('lead_id', filterLeadId);

      const queryString = params.toString();
      const url = queryString ? `/api/tarefas?${queryString}` : '/api/tarefas';

      const response = await fetchWithAuth(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Verificar se a resposta é JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Resposta não é JSON:', text.substring(0, 200));
        throw new Error('Servidor retornou resposta inválida. Verifique se o servidor está rodando.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || 'Erro ao buscar tarefas');
      }

      const data: Tarefa[] = await response.json();
      setTarefas(data);
    } catch (error: any) {
      console.error('Erro ao buscar tarefas:', error);
      // Não quebrar a UI, apenas definir array vazio
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPrioridade, filterEtapa, filterLeadId]);

  const buscarDetalhesTarefa = useCallback(async (tarefaId: string): Promise<Tarefa | null> => {
    try {
      const response = await fetchWithAuth(`/api/tarefas/${tarefaId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes da tarefa');
      }

      const dadosAtualizados: Tarefa = await response.json();
      return dadosAtualizados;
    } catch (error) {
      console.error('Erro ao carregar formulário da tarefa:', error);
      return null;
    }
  }, []);

  const carregarTarefaFormulario = useCallback(async (tarefaBase: Tarefa) => {
    setShowFormularioModal(true);
    setFormularioLoading(true);
    setTarefaSelecionada(tarefaBase);

    try {
      const dadosAtualizados = await buscarDetalhesTarefa(tarefaBase.id);
      if (dadosAtualizados) {
        setTarefaSelecionada(dadosAtualizados);
        setTarefas(prev => prev.map(t => t.id === dadosAtualizados.id ? { ...t, ...dadosAtualizados } : t));
      }
    } finally {
      setFormularioLoading(false);
    }
  }, [buscarDetalhesTarefa]);

  // Ler parâmetros da URL ao carregar a página
  useEffect(() => {
    const leadId = searchParams.get('lead_id');
    const statusParam = searchParams.get('status');
    
    if (leadId) {
      setFilterLeadId(leadId);
    }
    
    // Se vier com parâmetro de status, usar filtro customizado
    // Caso contrário, quando vem apenas com lead_id, mostrar todas as tarefas não concluídas
    if (statusParam) {
      // Quando vem com status específico (ex: pendente,atrasada,em_andamento)
      // Não usar o filtro padrão, mas filtrar no frontend após buscar
      setFilterStatus('all'); // Buscar todas e filtrar no frontend
    } else if (leadId) {
      // Quando vem apenas com lead_id, mostrar todas as tarefas não concluídas
      setFilterStatus('all');
    }
  }, [searchParams]);

  // Abrir tarefa específica quando tarefa_id estiver na URL
  useEffect(() => {
    const tarefaId = searchParams.get('tarefa_id');
    if (tarefaId && tarefas.length > 0 && !loading && !showFormularioModal) {
      const tarefa = tarefas.find(t => t.id === tarefaId);
      if (tarefa) {
        carregarTarefaFormulario(tarefa);
        // Remover o parâmetro da URL após abrir o modal para evitar reabertura
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('tarefa_id');
        const newSearch = newParams.toString();
        navigate(`/tarefas${newSearch ? `?${newSearch}` : ''}`, { replace: true });
      }
    }
  }, [tarefas, loading, searchParams, showFormularioModal, navigate, carregarTarefaFormulario]);

  useEffect(() => {
    fetchTarefas();
  }, [fetchTarefas]);

  const handleModalOpenChange = (open: boolean) => {
    setShowFormularioModal(open);
    if (!open) {
      setTarefaSelecionada(null);
      setFormularioLoading(false);
    }
  };

  const handleStatusChange = async (tarefaId: string, newStatus: Tarefa['status']) => {
    try {
      const tarefa = tarefas.find(t => t.id === tarefaId);
      
      // Se a tarefa tem formulário e está sendo marcada como concluída, abrir o formulário
      if (newStatus === 'concluida' || newStatus === 'concluida_com_pendencias') {
        if (tarefa) {
          setFormularioLoading(true);
          const detalhes = await buscarDetalhesTarefa(tarefa.id);
          setFormularioLoading(false);

          if (detalhes?.perguntas_formulario && detalhes.perguntas_formulario.length > 0) {
            setTarefas(prev => prev.map(t => t.id === detalhes.id ? { ...t, ...detalhes } : t));
            setTarefaSelecionada(detalhes);
            setShowFormularioModal(true);
            return;
          }
        }
      }

      const response = await fetchWithAuth(`/api/tarefas/${tarefaId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status da tarefa');
      }

      // Atualizar lista local
      setTarefas(prev => prev.map(t => 
        t.id === tarefaId ? { ...t, status: newStatus as any } : t
      ));
      
      // Disparar evento customizado para atualizar PipelineWidget quando tarefa é concluída
      if (newStatus === 'concluida' || newStatus === 'concluida_com_pendencias') {
        window.dispatchEvent(new CustomEvent('tarefaConcluida', { 
          detail: { tarefaId, leadId: tarefa?.lead?.id } 
        }));
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handleAbrirFormulario = (tarefa: Tarefa) => {
    carregarTarefaFormulario(tarefa);
  };

  const handleFormularioSalvo = () => {
    fetchTarefas();
    setShowFormularioModal(false);
    setTarefaSelecionada(null);
    setFormularioLoading(false);
  };

  const handleDeleteClick = (tarefaId: string) => {
    setTarefaParaDeletar(tarefaId);
  };

  const handleConfirmDelete = async () => {
    if (!tarefaParaDeletar) return;

    try {
      setDeletandoTarefa(true);
      const response = await fetchWithAuth(`/api/tarefas/${tarefaParaDeletar}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || 'Erro ao deletar tarefa');
      }

      // Verificar se a resposta foi bem-sucedida
      const result = await response.json().catch(() => ({}));
      
      // Se a tarefa deletada estava sendo visualizada, fechar o modal
      if (tarefaSelecionada?.id === tarefaParaDeletar) {
        setShowFormularioModal(false);
        setTarefaSelecionada(null);
      }

      // Recarregar a lista de tarefas do servidor para garantir sincronização
      await fetchTarefas();
    } catch (error: any) {
      console.error('Erro ao deletar tarefa:', error);
      alert(error.message || 'Erro ao deletar tarefa');
    } finally {
      setDeletandoTarefa(false);
      setTarefaParaDeletar(null);
    }
  };

  // Verificar se há filtro de status na URL
  const statusParam = searchParams.get('status');
  const statusesFiltrados = statusParam ? statusParam.split(',') : null;

  const filteredTarefas = tarefas.filter(tarefa => {
    // Se há filtro de status na URL, aplicar esse filtro primeiro
    if (statusesFiltrados && statusesFiltrados.length > 0) {
      if (!statusesFiltrados.includes(tarefa.status)) {
        return false;
      }
    } else {
      // Quando o filtro de status é "all", excluir tarefas concluídas
      if (filterStatus === 'all') {
        if (tarefa.status === 'concluida' || tarefa.status === 'concluida_com_pendencias') {
          return false;
        }
      } else if (filterStatus !== 'all') {
        // Aplicar filtro de status do componente se não houver filtro na URL
        if (tarefa.status !== filterStatus) {
          return false;
        }
      }
    }
    
    // Aplicar filtro de busca se houver query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        tarefa.titulo.toLowerCase().includes(query) ||
        tarefa.descricao?.toLowerCase().includes(query) ||
        tarefa.lead?.nome.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const tarefasParaQuadro = useMemo(() => {
    return tarefas.filter((tarefa) => {
      if (statusesFiltrados && statusesFiltrados.length > 0) {
        if (!statusesFiltrados.includes(tarefa.status)) return false;
      } else if (filterStatus !== 'all') {
        if (tarefa.status !== filterStatus) return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          tarefa.titulo.toLowerCase().includes(query) ||
          tarefa.descricao?.toLowerCase().includes(query) ||
          (tarefa.lead?.nome && tarefa.lead.nome.toLowerCase().includes(query))
        );
      }
      return true;
    });
  }, [tarefas, statusesFiltrados, filterStatus, searchQuery]);

  const tarefasPendentes = filteredTarefas.filter(t => t.status === 'pendente' || t.status === 'atrasada');

  // Estatísticas
  const totalTarefas = tarefas.length;
  const tarefasAtrasadas = tarefas.filter(t => t.esta_atrasada).length;
  const tarefasUrgentes = tarefas.filter(t => t.prioridade === 'urgente' && (t.status === 'pendente' || t.status === 'em_andamento')).length;

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Tarefas</h1>
          <p className="text-sm md:text-base text-muted-foreground">Gerencie suas tarefas pendentes no processo de vendas</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => setShowAdicionarTarefaModal(true)}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Adicionar Tarefa</span>
            <span className="sm:hidden">Adicionar</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFluxoModal(true)}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Ver Fluxo de Tarefas</span>
            <span className="sm:hidden">Fluxo</span>
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowGerenciarModal(true)}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Gerenciar Templates</span>
              <span className="sm:hidden">Templates</span>
            </Button>
          )}
          {!isMobile && (
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="rounded-r-none"
                title="Quadro Kanban"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
                title="Lista em tabela"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Modais */}
      <AdicionarTarefaModal
        open={showAdicionarTarefaModal}
        onOpenChange={setShowAdicionarTarefaModal}
        onSuccess={fetchTarefas}
      />
      <FluxoTarefasModal open={showFluxoModal} onOpenChange={setShowFluxoModal} />
      <GerenciarTemplatesModal open={showGerenciarModal} onOpenChange={setShowGerenciarModal} />
      <PreencherFormularioModal
        open={showFormularioModal}
        onOpenChange={handleModalOpenChange}
        tarefa={tarefaSelecionada}
        onSuccess={handleFormularioSalvo}
        isLoading={formularioLoading}
        onVerLead={(leadId) => navigate(`/clientes?id=${encodeURIComponent(leadId)}`)}
      />

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Tarefas</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTarefas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tarefasPendentes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Atrasadas</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tarefasAtrasadas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Urgentes</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tarefasUrgentes}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-xs md:text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar tarefas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs md:text-sm font-medium">Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="concluida">Concluída</SelectItem>
                  <SelectItem value="atrasada">Atrasada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-xs md:text-sm font-medium">Prioridade</label>
              <Select value={filterPrioridade} onValueChange={setFilterPrioridade}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as prioridades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-xs md:text-sm font-medium">Etapa</label>
              <Select value={filterEtapa} onValueChange={setFilterEtapa}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as etapas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="lead-capturado">Lead Capturado</SelectItem>
                  <SelectItem value="qualificacao">Qualificação</SelectItem>
                  <SelectItem value="analise-necessidades">Análise de Necessidades</SelectItem>
                  <SelectItem value="visita-tecnica">Visita Técnica</SelectItem>
                  <SelectItem value="proposta-valor">Proposta de Valor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quadro Kanban ou lista */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">Carregando tarefas...</div>
          </CardContent>
        </Card>
      ) : isMobile || viewMode === 'cards' ? (
        tarefasParaQuadro.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground">Nenhuma tarefa encontrada</div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            <p className="text-xs md:text-sm text-muted-foreground">
              Arraste os cartões entre colunas para alterar o status. Clique em um cartão para abrir o formulário.
            </p>
            <TarefasKanbanBoard
              tarefas={tarefasParaQuadro}
              onStatusChange={handleStatusChange}
              onOpenTarefa={handleAbrirFormulario}
              onDelete={handleDeleteClick}
              onVerLead={(leadId) => navigate(`/clientes?id=${leadId}`)}
              onAfterQuickCreate={fetchTarefas}
            />
          </div>
        )
      ) : filteredTarefas.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">Nenhuma tarefa encontrada</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div>
            <h2 className="text-lg md:text-xl font-semibold">Todas as Tarefas</h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              Clique em uma tarefa para visualizar o template completo, preencher o formulário e registrar as respostas do cliente.
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarefa</TableHead>
                  <TableHead>Etapa</TableHead>
                  <TableHead>Prioridade</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Vendedor</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTarefas.map((tarefa) => (
                  <TarefaListItem
                    key={tarefa.id}
                    tarefa={tarefa}
                    onClick={handleAbrirFormulario}
                    onStatusChange={handleStatusChange}
                    onVerLead={(leadId) => navigate(`/clientes?id=${leadId}`)}
                    onDelete={handleDeleteClick}
                    statusBadge={getStatusBadge(tarefa.status)}
                    prioridadeBadge={getPrioridadeBadge(tarefa.prioridade)}
                    isParceiro={false}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={tarefaParaDeletar !== null} onOpenChange={(open) => !open && setTarefaParaDeletar(null)}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-[425px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Confirmar Exclusão da Tarefa
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta tarefa?
              <br /><br />
              Esta ação é <strong>irreversível</strong> e irá remover permanentemente a tarefa e todos os dados associados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletandoTarefa}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deletandoTarefa}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletandoTarefa ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Excluindo...
                </>
              ) : (
                'Excluir Tarefa'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



